import { lazy } from "react";
import { Route } from "react-router-dom";
import { StaffRoute, AdminRoute } from "../components/ProtectedRoute";
import { useAuth } from "../context/AuthContext";

// Every staff/admin page, extracted out of App.js so it can be rendered once
// per OPEN TAB (see StaffTabKeepAliveShell) instead of once per navigation —
// that's what lets a tab stay exactly where the user left it (scroll,
// half-typed form, open filters/dropdowns) when they switch to another tab
// and back, like a real browser tab. Wrappers (StaffRoute/AdminRoute) are
// unchanged from their original App.js definitions.
const OperationManual = lazy(() => import("../pages/OperationManual"));
const LoanDetails = lazy(() => import("../pages/customer/LoanDetails"));

const StaffDashboard = lazy(() => import("../pages/staff/Dashboard"));
const NewTabLanding = lazy(() => import("../pages/staff/NewTabLanding"));
const LoanApplications = lazy(() => import("../pages/staff/Applications"));
const RejectedLoans = lazy(() => import("../pages/staff/RejectedLoans"));
const LoansHub = lazy(() => import("../pages/staff/LoansHub"));
const BankChangeRequests = lazy(() => import("../pages/staff/BankChangeRequests"));
const UpdateInfoRequests = lazy(() => import("../pages/staff/UpdateInfoRequests"));
const StaffUpdateRequests = lazy(() => import("../pages/staff/StaffUpdateRequests"));
const ApplicationReview = lazy(() => import("../pages/staff/ApplicationReview"));
const StaffLoans = lazy(() => import("../pages/staff/Loans"));
const StaffCreateLoan = lazy(() => import("../pages/staff/CreateLoan"));
const DisbursedLoans = lazy(() => import("../pages/staff/DisbursedLoans"));
const LoanApprovalDelay = lazy(() => import("../pages/staff/LoanApprovalDelay"));
const LoanWatchlist = lazy(() => import("../pages/staff/LoanWatchlist"));
const StaffPayments = lazy(() => import("../pages/staff/Payments"));
const LoanCalculatorPage = lazy(() => import("../pages/staff/LoanCalculatorPage"));
const StaffCustomers = lazy(() => import("../pages/staff/Customers"));
const StaffBlacklistReport = lazy(() => import("../pages/staff/BlacklistReport"));
const StaffBranches = lazy(() => import("../pages/staff/Branches"));
const StaffReports = lazy(() => import("../pages/staff/Reports"));
const StaffExpenses = lazy(() => import("../pages/staff/Expenses"));
const TransportJournal = lazy(() => import("../pages/staff/TransportJournal"));
const StaffTeam = lazy(() => import("../pages/staff/Team"));
const RolesPermissions = lazy(() => import("../pages/staff/RolesPermissions"));
const AppCrashes = lazy(() => import("../pages/staff/AppCrashes"));
const StaffProfile = lazy(() => import("../pages/staff/StaffProfile"));
const SocialProfile = lazy(() => import("../pages/staff/SocialProfile"));
const ProfileByRole = () => (useAuth().user?.role === 'social' ? <SocialProfile /> : <StaffProfile />);
const CustomerDetailPage = lazy(() => import("../pages/staff/CustomerDetailPage"));
const PotentialCustomerDetail = lazy(() => import("../pages/staff/PotentialCustomerDetail"));
const NewPotentialCustomer = lazy(() => import("../pages/staff/NewPotentialCustomer"));
const Recruitment = lazy(() => import("../pages/staff/Recruitment"));
const SystemAlerts = lazy(() => import("../pages/staff/SystemAlerts"));
const ChatFileCleanup = lazy(() => import("../pages/admin/ChatFileCleanup"));
const RecruitmentApplicationDetail = lazy(() => import("../pages/staff/RecruitmentApplicationDetail"));
const CustomerBroadcasts = lazy(() => import("../pages/staff/CustomerBroadcasts"));
const MessageDrafts = lazy(() => import("../pages/staff/MessageDrafts"));
const StaffAnalytics = lazy(() => import("../pages/staff/Analytics"));
const StaffActivityLogs = lazy(() => import("../pages/staff/ActivityLogs"));
const StaffIncome = lazy(() => import("../pages/staff/Income"));
const MyAttendance = lazy(() => import("../pages/staff/MyAttendance"));
const MyLeaveRequest = lazy(() => import("../pages/staff/MyLeaveRequest"));
const MobileBalances = lazy(() => import("../pages/staff/MobileBalances"));
const AttendanceRegister = lazy(() => import("../pages/staff/AttendanceRegister"));
const MyStaffLoan = lazy(() => import("../pages/staff/MyStaffLoan"));
const MyPayslips = lazy(() => import("../pages/staff/MyPayslips"));
const RefundManagement = lazy(() => import("../pages/staff/RefundManagement"));
const AlertDetail = lazy(() => import("../pages/staff/refund_tabs/AlertDetail"));
const StaffLoanSettings = lazy(() => import("../pages/staff/StaffLoanSettings"));
const TransactionReports = lazy(() => import("../pages/staff/TransactionReports"));
const MonthlyStatistics = lazy(() => import("../pages/staff/MonthlyStatistics"));
const Marketing = lazy(() => import("../pages/staff/Marketing"));
const NewPlanPage = lazy(() => import("../pages/staff/marketing/NewPlanPage"));
const ExecutionReportPage = lazy(() => import("../pages/staff/marketing/ExecutionReportPage"));
const LoanReconciliation = lazy(() => import("../pages/staff/LoanReconciliation"));
const CustomerTransactions = lazy(() => import("../pages/staff/CustomerTransactions"));
const PettyCash = lazy(() => import("../pages/staff/PettyCash"));
const ExpenseAnalytics = lazy(() => import("../pages/staff/ExpenseAnalytics"));
const SocialContacts = lazy(() => import("../pages/staff/SocialContacts"));
const Contacts = lazy(() => import("../pages/staff/Contacts"));
const KeyContacts = lazy(() => import("../pages/staff/KeyContacts"));
const ActionPage = lazy(() => import("../pages/staff/ActionPage"));
const Navigation = lazy(() => import("../pages/staff/Navigation"));
const ExpenseHeads = lazy(() => import("../pages/staff/ExpenseHeads"));
const FeeManagement = lazy(() => import("../pages/admin/FeeManagement"));
const AdminSettings = lazy(() => import("../pages/admin/Settings"));
const Backups = lazy(() => import("../pages/admin/Backups"));
const StaffLoanManagement = lazy(() => import("../pages/admin/StaffLoanManagement"));
const StaffLoanHistory = lazy(() => import("../pages/admin/StaffLoanHistory"));
const GeneralLedger = lazy(() => import("../pages/admin/GeneralLedger"));
const PayrollManagement = lazy(() => import("../pages/admin/PayrollManagement"));
const PerformanceReports = lazy(() => import("../pages/admin/PerformanceReports"));
const StaffRegistrations = lazy(() => import("../pages/admin/StaffRegistrations"));
const ProfitLossReport = lazy(() => import("../pages/staff/ProfitLossReport"));
const ScheduledJobs = lazy(() => import("../pages/staff/ScheduledJobs"));
const ScheduledReports = lazy(() => import("../pages/staff/ScheduledReports"));
const AdminDataHealth = lazy(() => import("../pages/admin/AdminDataHealth"));
const NotificationSettings = lazy(() => import("../pages/staff/NotificationSettings"));
const StaffSettings = lazy(() => import("../pages/staff/Settings"));
const Appearance = lazy(() => import("../pages/admin/Appearance"));
const TaskRoleAssignments = lazy(() => import("../pages/admin/TaskRoleAssignments"));
const SecuritySettings = lazy(() => import("../pages/admin/SecuritySettings"));
const RequiredDocuments = lazy(() => import("../pages/admin/RequiredDocuments"));
const ActiveOTPs = lazy(() => import("../pages/admin/ActiveOTPs"));
const PendingRegistrations = lazy(() => import("../pages/admin/PendingRegistrations"));
const LoanSettings = lazy(() => import("../pages/admin/LoanSettings"));
const LoanFees = lazy(() => import("../pages/admin/LoanFees"));
const FixApplicationFees = lazy(() => import("../pages/admin/FixApplicationFees"));
const EmailTemplates = lazy(() => import("../pages/admin/EmailTemplates"));
const MessageFooters = lazy(() => import("../pages/admin/MessageFooters"));
const RegistrationSettings = lazy(() => import("../pages/admin/RegistrationSettings"));

const RiskWatchlist = lazy(() => import("../pages/staff/RiskWatchlist"));

const AdminInvestors = lazy(() => import("../pages/admin/investors/Investors"));
const AdminInvestorDetail = lazy(() => import("../pages/admin/investors/InvestorDetail"));
const AdminInvestmentProducts = lazy(() => import("../pages/admin/investors/Products"));
const AdminInvestments = lazy(() => import("../pages/admin/investors/Investments"));
const AdminWithdrawals = lazy(() => import("../pages/admin/investors/Withdrawals"));
const AdminPeople = lazy(() => import("../pages/admin/investors/People"));
const AdminAddPerson = lazy(() => import("../pages/admin/investors/AddPerson"));
const AdminPaymentCalculation = lazy(() => import("../pages/admin/investors/PaymentCalculation"));
const AdminMonthlyInterest = lazy(() => import("../pages/admin/investors/MonthlyInterestPayments"));
const InvestmentHub = lazy(() => import("../pages/admin/investors/InvestmentHub"));

const StaffCalendar = lazy(() => import("../pages/staff/Calendar"));

const MassAction = lazy(() => import("../pages/staff/MassAction"));
const Chat = lazy(() => import("../pages/staff/Chat"));
const ChatAdmin = lazy(() => import("../pages/staff/ChatAdmin"));
const SupportQueue = lazy(() => import("../pages/staff/SupportQueue"));

// Rendered inside a per-tab <Routes location={...}> in StaffTabKeepAliveShell
// — must stay a flat list of <Route> elements (no wrapping <Routes>/<div> of
// its own) so the parent's location override applies correctly.
export default function StaffRoutesList() {
  return (
    <>
      <Route path="/staff/new-tab/:tabKey" element={
        <StaffRoute><NewTabLanding /></StaffRoute>
      } />
      <Route path="/staff/dashboard" element={
        <StaffRoute><StaffDashboard /></StaffRoute>
      } />
      <Route path="/staff/risk-watchlist" element={
        <StaffRoute><RiskWatchlist /></StaffRoute>
      } />
      <Route path="/staff/applications" element={
        <StaffRoute><LoanApplications /></StaffRoute>
      } />
      <Route path="/staff/rejected-loans" element={
        <StaffRoute><RejectedLoans /></StaffRoute>
      } />
      <Route path="/staff/loans-hub" element={
        <StaffRoute><LoansHub /></StaffRoute>
      } />
      <Route path="/staff/bank-changes" element={
        <StaffRoute><BankChangeRequests /></StaffRoute>
      } />
      <Route path="/staff/update-requests" element={
        <StaffRoute><UpdateInfoRequests /></StaffRoute>
      } />
      <Route path="/staff/staff-update-requests" element={
        <StaffRoute><StaffUpdateRequests /></StaffRoute>
      } />
      <Route path="/staff/applications/:applicationId" element={
        <StaffRoute><ApplicationReview /></StaffRoute>
      } />
      <Route path="/staff/loans" element={
        <StaffRoute><StaffLoans /></StaffRoute>
      } />
      <Route path="/staff/create-loan" element={
        <StaffRoute><StaffCreateLoan /></StaffRoute>
      } />
      <Route path="/staff/disbursed-loans" element={
        <StaffRoute><DisbursedLoans /></StaffRoute>
      } />
      <Route path="/staff/loan-approval-delay" element={
        <StaffRoute><LoanApprovalDelay /></StaffRoute>
      } />
      <Route path="/staff/loan-watchlist" element={
        <StaffRoute><LoanWatchlist /></StaffRoute>
      } />
      <Route path="/staff/loans/:loanId" element={
        <StaffRoute><LoanDetails /></StaffRoute>
      } />
      <Route path="/staff/payments" element={
        <StaffRoute><StaffPayments /></StaffRoute>
      } />
      <Route path="/staff/loan-calculator" element={
        <StaffRoute><LoanCalculatorPage /></StaffRoute>
      } />
      <Route path="/staff/customers" element={
        <StaffRoute><StaffCustomers /></StaffRoute>
      } />
      <Route path="/staff/blacklist-report" element={
        <StaffRoute><StaffBlacklistReport /></StaffRoute>
      } />
      <Route path="/staff/customers/broadcasts" element={
        <StaffRoute><CustomerBroadcasts /></StaffRoute>
      } />
      <Route path="/staff/message-drafts" element={
        <StaffRoute><MessageDrafts /></StaffRoute>
      } />
      <Route path="/staff/potential-customers/new" element={
        <StaffRoute><NewPotentialCustomer /></StaffRoute>
      } />
      <Route path="/staff/potential-customers/:pid" element={
        <StaffRoute><PotentialCustomerDetail /></StaffRoute>
      } />
      <Route path="/staff/recruitment" element={
        <StaffRoute><Recruitment /></StaffRoute>
      } />
      <Route path="/staff/system-alerts" element={
        <AdminRoute><SystemAlerts /></AdminRoute>
      } />
      <Route path="/admin/chat-files" element={
        <AdminRoute><ChatFileCleanup /></AdminRoute>
      } />
      <Route path="/staff/recruitment/applications/:applicationId" element={
        <StaffRoute><RecruitmentApplicationDetail /></StaffRoute>
      } />
      <Route path="/staff/customers/:customerId" element={
        <StaffRoute><CustomerDetailPage /></StaffRoute>
      } />
      <Route path="/staff/branches" element={
        <StaffRoute><StaffBranches /></StaffRoute>
      } />
      <Route path="/staff/team" element={
        <StaffRoute><StaffTeam /></StaffRoute>
      } />
      <Route path="/staff/roles" element={
        <AdminRoute><RolesPermissions /></AdminRoute>
      } />
      <Route path="/staff/app-crashes" element={
        <AdminRoute><AppCrashes /></AdminRoute>
      } />
      <Route path="/staff/team/loan-settings" element={
        <StaffRoute><StaffLoanSettings /></StaffRoute>
      } />
      <Route path="/staff/profile" element={
        <StaffRoute><ProfileByRole /></StaffRoute>
      } />
      <Route path="/staff/team/:userId" element={
        <StaffRoute><StaffProfile /></StaffRoute>
      } />
      <Route path="/staff/expenses" element={
        <StaffRoute><StaffExpenses /></StaffRoute>
      } />
      <Route path="/staff/transport-journal" element={
        <StaffRoute><TransportJournal /></StaffRoute>
      } />
      <Route path="/staff/petty-cash" element={
        <StaffRoute><PettyCash /></StaffRoute>
      } />
      <Route path="/staff/social-contacts" element={
        <StaffRoute><SocialContacts /></StaffRoute>
      } />
      <Route path="/staff/contacts" element={
        <StaffRoute><Contacts /></StaffRoute>
      } />
      <Route path="/staff/key-contacts" element={
        <StaffRoute><KeyContacts /></StaffRoute>
      } />
      <Route path="/staff/actions" element={
        <StaffRoute><ActionPage /></StaffRoute>
      } />
      <Route path="/staff/navigation" element={
        <StaffRoute><Navigation /></StaffRoute>
      } />
      <Route path="/staff/expense-analytics" element={
        <StaffRoute><ExpenseAnalytics /></StaffRoute>
      } />
      <Route path="/staff/expense-heads" element={
        <StaffRoute><ExpenseHeads /></StaffRoute>
      } />
      <Route path="/staff/reports" element={
        <StaffRoute><StaffReports /></StaffRoute>
      } />
      <Route path="/staff/analytics" element={
        <StaffRoute><StaffAnalytics /></StaffRoute>
      } />
      <Route path="/staff/activity-logs" element={
        <StaffRoute><StaffActivityLogs /></StaffRoute>
      } />
      <Route path="/staff/income" element={
        <StaffRoute><StaffIncome /></StaffRoute>
      } />
      <Route path="/staff/my-attendance" element={
        <StaffRoute><MyAttendance /></StaffRoute>
      } />
      <Route path="/staff/my-leave" element={
        <StaffRoute><MyLeaveRequest /></StaffRoute>
      } />
      <Route path="/staff/mobile-balances" element={
        <StaffRoute><MobileBalances /></StaffRoute>
      } />
      <Route path="/staff/manual" element={
        <StaffRoute><OperationManual /></StaffRoute>
      } />
      <Route path="/staff/attendance" element={
        <StaffRoute><AttendanceRegister /></StaffRoute>
      } />
      <Route path="/staff/my-loan" element={
        <StaffRoute><MyStaffLoan /></StaffRoute>
      } />
      <Route path="/admin/settings" element={
        <StaffRoute><AdminSettings /></StaffRoute>
      } />
      <Route path="/admin/loan-settings" element={
        <StaffRoute><LoanSettings /></StaffRoute>
      } />
      <Route path="/admin/loan-fees" element={
        <StaffRoute><LoanFees /></StaffRoute>
      } />
      <Route path="/admin/fix-application-fees" element={
        <StaffRoute><FixApplicationFees /></StaffRoute>
      } />
      <Route path="/admin/email-templates" element={
        <StaffRoute><EmailTemplates /></StaffRoute>
      } />
      <Route path="/admin/message-footers" element={
        <AdminRoute><MessageFooters /></AdminRoute>
      } />
      <Route path="/admin/staff-loans" element={
        <AdminRoute><StaffLoanManagement /></AdminRoute>
      } />
      <Route path="/admin/staff-loans/history/:userId" element={
        <AdminRoute><StaffLoanHistory /></AdminRoute>
      } />
      <Route path="/admin/general-ledger" element={
        <AdminRoute><GeneralLedger /></AdminRoute>
      } />
      <Route path="/staff/my-payslips" element={
        <StaffRoute><MyPayslips /></StaffRoute>
      } />
      <Route path="/admin/payroll" element={
        <StaffRoute><PayrollManagement /></StaffRoute>
      } />
      <Route path="/admin/performance-reports" element={
        <StaffRoute><PerformanceReports /></StaffRoute>
      } />
      <Route path="/staff/refunds" element={
        <StaffRoute><RefundManagement /></StaffRoute>
      } />
      <Route path="/staff/refunds/alerts/:alertId" element={
        <StaffRoute><AlertDetail /></StaffRoute>
      } />
      <Route path="/staff/reports/transactions" element={
        <StaffRoute><TransactionReports /></StaffRoute>
      } />
      <Route path="/staff/reports/monthly-statistics" element={
        <StaffRoute><MonthlyStatistics /></StaffRoute>
      } />
      <Route path="/staff/marketing" element={
        <StaffRoute><Marketing /></StaffRoute>
      } />
      <Route path="/staff/marketing/plan/new" element={
        <StaffRoute><NewPlanPage /></StaffRoute>
      } />
      <Route path="/staff/marketing/plan/:planId/edit" element={
        <StaffRoute><NewPlanPage /></StaffRoute>
      } />
      <Route path="/staff/marketing/report/new/:planId" element={
        <StaffRoute><ExecutionReportPage mode="create" /></StaffRoute>
      } />
      <Route path="/staff/marketing/report/:reportId" element={
        <StaffRoute><ExecutionReportPage mode="detail" /></StaffRoute>
      } />
      <Route path="/staff/loans/reconciliation" element={
        <StaffRoute><LoanReconciliation /></StaffRoute>
      } />
      <Route path="/staff/loans/reconciliation/:reportId" element={
        <StaffRoute><LoanReconciliation /></StaffRoute>
      } />
      <Route path="/staff/mass-action" element={
        <StaffRoute><MassAction /></StaffRoute>
      } />
      <Route path="/staff/customers/:customerId/transactions" element={
        <StaffRoute><CustomerTransactions /></StaffRoute>
      } />
      <Route path="/staff/fees" element={
        <StaffRoute><FeeManagement /></StaffRoute>
      } />
      <Route path="/admin/staff-registrations" element={
        <StaffRoute><StaffRegistrations /></StaffRoute>
      } />
      <Route path="/staff/reports/profit-loss" element={
        <StaffRoute><ProfitLossReport /></StaffRoute>
      } />
      <Route path="/admin/scheduled-jobs" element={
        <StaffRoute><ScheduledJobs /></StaffRoute>
      } />
      <Route path="/admin/data-health" element={
        <StaffRoute><AdminDataHealth /></StaffRoute>
      } />
      <Route path="/admin/scheduled-reports" element={
        <StaffRoute><ScheduledReports /></StaffRoute>
      } />
      <Route path="/staff/notification-settings" element={
        <StaffRoute><NotificationSettings /></StaffRoute>
      } />
      <Route path="/staff/settings" element={
        <StaffRoute><StaffSettings /></StaffRoute>
      } />
      <Route path="/staff/calendar" element={
        <StaffRoute><StaffCalendar /></StaffRoute>
      } />
      <Route path="/admin/appearance" element={
        <StaffRoute><Appearance /></StaffRoute>
      } />
      <Route path="/admin/task-roles" element={
        <StaffRoute><TaskRoleAssignments /></StaffRoute>
      } />
      <Route path="/admin/security" element={
        <StaffRoute><SecuritySettings /></StaffRoute>
      } />
      <Route path="/admin/backups" element={
        <StaffRoute><Backups /></StaffRoute>
      } />
      <Route path="/admin/required-documents" element={
        <StaffRoute><RequiredDocuments /></StaffRoute>
      } />
      <Route path="/admin/registration-settings" element={
        <StaffRoute><RegistrationSettings /></StaffRoute>
      } />
      <Route path="/admin/active-otps" element={
        <StaffRoute><ActiveOTPs /></StaffRoute>
      } />
      <Route path="/admin/pending-registrations" element={
        <StaffRoute><PendingRegistrations /></StaffRoute>
      } />

      {/* Admin Investor Management Routes */}
      <Route path="/admin/investors" element={
        <StaffRoute><AdminInvestors /></StaffRoute>
      } />
      <Route path="/admin/investors/detail" element={
        <StaffRoute><AdminInvestorDetail /></StaffRoute>
      } />
      <Route path="/admin/investment-products" element={
        <StaffRoute><AdminInvestmentProducts /></StaffRoute>
      } />
      <Route path="/admin/investments" element={
        <StaffRoute><AdminInvestments /></StaffRoute>
      } />
      <Route path="/admin/withdrawals" element={
        <StaffRoute><AdminWithdrawals /></StaffRoute>
      } />
      <Route path="/admin/people" element={
        <StaffRoute><AdminPeople /></StaffRoute>
      } />
      <Route path="/admin/people/add" element={
        <StaffRoute><AdminAddPerson /></StaffRoute>
      } />
      <Route path="/admin/people/calculate" element={
        <StaffRoute><AdminPaymentCalculation /></StaffRoute>
      } />
      <Route path="/admin/interest-payments" element={
        <StaffRoute><AdminMonthlyInterest /></StaffRoute>
      } />
      <Route path="/admin/investment-hub" element={
        <StaffRoute><InvestmentHub /></StaffRoute>
      } />

      {/* Chat & Calls */}
      <Route path="/staff/chat" element={
        <StaffRoute><Chat /></StaffRoute>
      } />
      <Route path="/staff/chat-admin" element={
        <StaffRoute><ChatAdmin /></StaffRoute>
      } />
      <Route path="/staff/support-queue" element={
        <StaffRoute><SupportQueue /></StaffRoute>
      } />
    </>
  );
}
